import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  Button,
  Divider,
  Spinner,
  FormControl,
  FormLabel,
  useToast,
  IconButton,
  Link as ChakraLink,
  Tag,
  TagLabel,
  Avatar,
  Tooltip,
  Badge,
  Flex,
  Select,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  SimpleGrid,
  useDisclosure,
  Grid,
  GridItem,
  Wrap,
  WrapItem,
  Skeleton,
  SkeletonText,
} from '@chakra-ui/react';
import { AttachmentIcon, DeleteIcon, InfoOutlineIcon, ChevronUpIcon, ChevronDownIcon, EditIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  useTask,
  useTaskComments,
  useAddComment,
  useDeleteComment,
  useEditComment,
  useTaskFiles,
  useUploadFile,
  useDeleteFile,
  useAddReply,
  useDeleteReply,
  useEditReply,
  useTasks,
  useUpdateTask,
} from '../hooks/useTasks';
import { CreateTaskData, Task, UpdateTaskData } from '../api/taskApi';
import TaskModal from '../components/modals/TaskBuiltInFormModal';
import { useAuthenticatedUser } from '../contexts/AuthenticatedUserInfoContext';
import { canModifyTask, Permission } from '../utils/permissions';
import { usePermissions, PermissionGuard } from '../components/PermissionGuard';
import { useProjects } from '../hooks/useProjects';
import { userApi } from '../api/userApi';
import {
  getTaskDetailsPreferences,
  updateTaskDetailsPreferences,
  TaskDetailsPreferences,
} from '../utils/localStorage';
import CommentsSection from '../components/CommentsSection';
import SubtaskManagement from '../components/SubtaskManagement';
import { saveCloudinaryTaskFile } from '../api/taskFileApi';
import { TaskMentionDisplay, initializeTaskNavigation } from '../components/TaskMentionDisplay';

interface TaskDetailsProps {
  taskId: string;
  onClose: () => void;
  showShadow?: boolean;
  showHeaderButtons?: boolean;
  showTopHeader?: boolean;
}

type UserRef = string | { _id: string; firstName?: string; lastName?: string; email?: string };

const TaskDetails: React.FC<TaskDetailsProps> = ({ 
  taskId, onClose, showShadow = false, showTopHeader = true , showHeaderButtons = true
}) => {
  // Load preferences from localStorage
  const [preferences, setPreferences] = useState<TaskDetailsPreferences>(getTaskDetailsPreferences());
  
  const [comment, setComment] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [users, setUsers] = useState<Record<string, any>>({});
  const [commentsExpanded, setCommentsExpanded] = useState(preferences.commentsExpanded);
  const [filesExpanded, setFilesExpanded] = useState(preferences.filesExpanded);
  const [commentSortOrder, setCommentSortOrder] = useState<'asc' | 'desc'>(preferences.commentSortOrder);
  const [editorHeight, setEditorHeight] = useState(preferences.editorHeight);
  
  const toast = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const subtleBorder = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const insetBg = useColorModeValue('gray.50', 'gray.700');

  const { user } = useAuth();
  const { canPerformWrites } = usePermissions();
  const { data: task, isLoading: loadingTask } = useTask(taskId);
  const { data: projects } = useProjects();
  const { data: allTasks } = useTasks(true); // Fetch all tasks for mentions
  const { data: comments = [], refetch: refetchComments } = useTaskComments(taskId);
  const addCommentMutation = useAddComment(taskId);
  const deleteCommentMutation = useDeleteComment(taskId);
  const { data: files = [], refetch: refetchFiles } = useTaskFiles(taskId);
  const uploadFileMutation = useUploadFile(taskId);
  const deleteFileMutation = useDeleteFile(taskId);
  const addReplyMutation = useAddReply(taskId);
  const deleteReplyMutation = useDeleteReply(taskId);
  const editReplyMutation = useEditReply(taskId);
  const editCommentMutation = useEditComment(taskId);
  const updateTaskMutation = useUpdateTask();
  const { authenticatedUser } = useAuthenticatedUser();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const [taskFormData, setTaskFormData] = useState<CreateTaskData>({
    taskTitle: '',
    priority: 'medium',
    description: '',
    type: 'development',
    status: 'Not Started',
    authBy: [],
    quotedPrice: undefined,
    dateStarted: undefined,
    dueDate: undefined,
    assignedToUser: [],
  });
  const [selectedProject, setSelectedProject] = useState<any | null>(null);

  const toDateInputValue = (date: string | Date | undefined) => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const toUserIds = (refs?: (string | { _id: string })[]): string[] =>
    (refs || []).map((ref) => (typeof ref === 'string' ? ref : ref._id));

  const isUserAssignedOrOwner = (currentTask: Task) => {
    if (!authenticatedUser) return false;

    if (currentTask.createdBy) {
      if (typeof currentTask.createdBy === 'string' && currentTask.createdBy === authenticatedUser.LoggedInUserId) {
        return true;
      }
      if (
        typeof currentTask.createdBy === 'object' &&
        currentTask.createdBy._id === authenticatedUser.LoggedInUserId
      ) {
        return true;
      }
    }

    if (!currentTask.assignedToUser) return false;

    return (currentTask.assignedToUser as (string | { _id: string })[]).some((u) => {
      if (typeof u === 'string') return u === authenticatedUser.LoggedInUserId;
      return u?._id === authenticatedUser.LoggedInUserId;
    });
  };

  const canEditCurrentTask = task ? canModifyTask(authenticatedUser, isUserAssignedOrOwner(task)) : false;

  const handleEditTask = () => {
    if (!task) return;

    setTaskFormData({
      taskTitle: task.taskTitle || '',
      priority: task.priority,
      description: task.description,
      type: task.type,
      status: task.status,
      authBy: toUserIds(task.authBy as (string | { _id: string })[] | undefined),
      quotedPrice: task.quotedPrice,
      agreedPrice: task.agreedPrice,
      dateRequested: toDateInputValue(task.dateRequested),
      dateStarted: toDateInputValue(task.dateStarted),
      dueDate: toDateInputValue(task.dueDate),
      assignedToUser: toUserIds(task.assignedToUser as (string | { _id: string })[] | undefined),
    });

    if (task.project) {
      if (typeof task.project === 'object') {
        setSelectedProject(task.project);
      } else {
        const project = projects?.find((p) => p._id === task.project);
        setSelectedProject(project || null);
      }
    } else {
      setSelectedProject(null);
    }

    onEditModalOpen();
  };

  const handleSubmitTask = async () => {
    if (!task) return;

    if (
      !taskFormData.taskTitle.trim() ||
      !taskFormData.priority ||
      !taskFormData.description.trim() ||
      !taskFormData.type
    ) {
      toast({
        title: 'Error',
        description: 'All required fields must be filled.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    let extraFields: Record<string, unknown> = {};
    if (
      taskFormData.status === 'Completed' &&
      task.status !== 'Completed' &&
      authenticatedUser?.LoggedInUserId
    ) {
      extraFields = { completedBy: authenticatedUser.LoggedInUserId };
    }

    const taskDataWithProject = {
      ...taskFormData,
      ...extraFields,
      project: selectedProject?._id || (typeof task.project === 'string' ? task.project : task.project?._id),
      assignedToUser: Array.isArray(taskFormData.assignedToUser) ? taskFormData.assignedToUser : [],
      dateStarted: taskFormData.dateStarted ? new Date(taskFormData.dateStarted) : undefined,
      dueDate: taskFormData.dueDate ? new Date(taskFormData.dueDate) : undefined,
    };

    try {
      await updateTaskMutation.mutateAsync({
        id: task._id,
        data: taskDataWithProject as UpdateTaskData,
      });
      toast({
        title: 'Task Updated',
        description: 'Task has been updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onEditModalClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update task. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const { users: allUsers } = await userApi.getAllUsers();
      const userMap: Record<string, any> = {};
      allUsers.forEach((u: any) => {
        userMap[u._id] = u;
      });
      setUsers(userMap);
    };
    fetchUsers();
  }, [taskId]);

  // Load saved comment content when component mounts
  useEffect(() => {
    if (preferences.lastCommentContent) {
      setComment(preferences.lastCommentContent);
    }
  }, [preferences.lastCommentContent]);

  // Initialize task navigation for mentions
  useEffect(() => {
    initializeTaskNavigation(navigate);
  }, [navigate]);

  // Save preferences when they change
  const updatePreference = (updates: Partial<TaskDetailsPreferences>) => {
    const newPreferences = { ...preferences, ...updates };
    setPreferences(newPreferences);
    updateTaskDetailsPreferences(updates);
  };

  // Handle expanding/collapsing sections
  const toggleCommentsExpanded = () => {
    const newState = !commentsExpanded;
    setCommentsExpanded(newState);
    updatePreference({ commentsExpanded: newState });
  };

  const toggleFilesExpanded = () => {
    const newState = !filesExpanded;
    setFilesExpanded(newState);
    updatePreference({ filesExpanded: newState });
  };

  const handleCommentSortChange = (newOrder: 'asc' | 'desc') => {
    setCommentSortOrder(newOrder);
    updatePreference({ commentSortOrder: newOrder });
  };

  const handleEditorHeightChange = (newHeight: number) => {
    setEditorHeight(newHeight);
    updatePreference({ editorHeight: newHeight });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  // Cloudinary widget upload for task files
  const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'all_files_uploads';
  const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dwnbctmwk';

  // Modal state for file description
  const [isFileDescModalOpen, setIsFileDescModalOpen] = useState(false);
  const [pendingFileUrl, setPendingFileUrl] = useState<string | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [pendingFileInfo, setPendingFileInfo] = useState<any>(null);
  const [pendingFileDesc, setPendingFileDesc] = useState('');
  const [pendingFiles, setPendingFiles] = useState<any[]>([]);
  const [isBatchUploading, setIsBatchUploading] = useState(false);

  const handleCloudinaryTaskFileUpload = () => {
    setPendingFiles([]); // Reset pending files
    setIsBatchUploading(false);
    
    window.cloudinary.openUploadWidget(
      {
        cloudName: CLOUDINARY_CLOUD_NAME,
        uploadPreset: CLOUDINARY_UPLOAD_PRESET,
        sources: ['local', 'url', 'camera'],
        multiple: true, // Allow multiple file uploads
        maxFiles: 10, // Limit to 10 files at once
        resourceType: 'auto', // Allow all file types
        clientAllowedFormats: ['pdf', 'doc', 'docx', 'txt', 'jpg', 'png', 'gif', 'bmp', 'webp'],
      },
      (error: any, result: any) => {
        if (error) {
          toast({ title: 'Cloudinary upload failed', description: error.message, status: 'error', duration: 4000 });
          return;
        }
        if (result.event === 'success') {
          const info = result.info;
          const fileInfo = {
            url: info.secure_url,
            filename: info.public_id,
            mimetype: info.resource_type + '/' + info.format,
            size: info.bytes,
            originalname: info.original_filename,
            fileDescription: '', // Default empty description
            id: info.public_id + Date.now(), // Unique ID for tracking
          };
          
          setPendingFiles(prev => [...prev, fileInfo]);
        }
        if (result.event === 'queues-end') {
          // All files have been uploaded to Cloudinary, show batch modal
          setIsFileDescModalOpen(true);
          // Don't set isBatchUploading to true here - only when actually saving
        }
      }
    );
  };

  const handleDeleteFile = async (fileId: string) => {
    await deleteFileMutation.mutateAsync(fileId);
    refetchFiles();
    toast({ title: 'File deleted', status: 'info', duration: 2000 });
  };

  const handleUpdateFileDescription = (fileId: string, description: string) => {
    setPendingFiles(prev => 
      prev.map(file => 
        file.id === fileId ? { ...file, fileDescription: description } : file
      )
    );
  };

  const handleSaveBatchFiles = async () => {
    setIsBatchUploading(true);
    try {
      for (const file of pendingFiles) {
        await saveCloudinaryTaskFile(taskId, {
          url: file.url,
          filename: file.filename,
          mimetype: file.mimetype,
          size: file.size,
          originalname: file.originalname,
          fileDescription: file.fileDescription,
        });
      }
      
      setIsFileDescModalOpen(false);
      setPendingFiles([]);
      setPendingFileUrl(null);
      setPendingFileInfo(null);
      setPendingFileDesc('');
      refetchFiles();
      toast({ 
        title: `${pendingFiles.length} files uploaded successfully`, 
        status: 'success', 
        duration: 3000 
      });
    } catch (err: any) {
      toast({ 
        title: 'Failed to save files', 
        description: err?.response?.data?.message || err.message, 
        status: 'error', 
        duration: 4000 
      });
    } finally {
      setIsBatchUploading(false);
    }
  };

  if (loadingTask) {
    return (
      <Box w="100%" p={{ base: 4, md: 8 }} bg={pageBg}>
        <Box bg={cardBg} borderRadius="2xl" p={8} boxShadow="sm" border="1px solid" borderColor={subtleBorder}>
          <VStack spacing={6} align="stretch">
            <Skeleton height="40px" width="60%" />
            <Skeleton height="24px" width="40%" />
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height="120px" borderRadius="xl" />
              ))}
            </SimpleGrid>
            <SkeletonText noOfLines={6} spacing={4} />
          </VStack>
        </Box>
      </Box>
    );
  }
  if (!task) {
    return (
      <Box w="100%" p={8} bg={pageBg}>
        <Box bg={cardBg} borderRadius="2xl" p={10} textAlign="center" border="1px solid" borderColor={subtleBorder}>
          <Heading size="md" mb={2}>Task not found</Heading>
          <Text color={mutedText} mb={6}>This task may have been deleted or you may not have access.</Text>
          <Button leftIcon={<ArrowBackIcon />} onClick={onClose} colorScheme="blue" borderRadius="full">
            Back to Tasks
          </Button>
        </Box>
      </Box>
    );
  }

  // Sort comments based on user preference
  const sortedComments = [...comments].sort((a, b) => {
    const timeA = new Date(a.createdAt).getTime();
    const timeB = new Date(b.createdAt).getTime();
    return commentSortOrder === 'desc' ? timeB - timeA : timeA - timeB;
  });

  // Helper function to get project name
  const getProjectName = (task: any) => {
    // If task has no project
    if (!task.project) return 'No Project';

    // If project is populated (object)
    if (
      typeof task.project === 'object' &&
      task.project.projectNumber &&
      task.project.projectName
    ) {
      return `${task.project.projectNumber} - ${task.project.projectName}`;
    }

    // If project is just an ID string, find it in the projects list
    if (typeof task.project === 'string') {
      const project = projects?.find(p => p._id === task.project);
      return project ? `${project.projectNumber} - ${project.projectName}` : 'Unknown Project';
    }

    return 'No Project';
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'green';
      case 'in progress':
        return 'blue';
      case 'pending':
        return 'orange';
      case 'on hold':
        return 'red';
      case 'not started':
        return 'gray';
      default:
        return 'purple';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const formatDate = (date?: string | Date) =>
    date ? new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const formatCurrency = (value?: number) =>
    value != null ? `$${value.toLocaleString()}` : '—';

  const getUserId = (userRef: UserRef) =>
    typeof userRef === 'string' ? userRef : userRef._id;

  const renderUserTag = (userRef: UserRef) => {
    const uid = getUserId(userRef);
    const userData =
      typeof userRef === 'object' && userRef.firstName
        ? userRef
        : users[uid];

    if (!userData) {
      return (
        <Tag size="md" key={uid} colorScheme="gray" borderRadius="full">
          <TagLabel>Unknown user</TagLabel>
        </Tag>
      );
    }

    return (
      <Tag size="md" key={uid} colorScheme="blue" borderRadius="full" variant="subtle">
        <Avatar
          size="2xs"
          name={`${userData.firstName} ${userData.lastName}`}
          src={(userData as any)?.avatarUrl}
          mr={1}
        />
        <TagLabel>
          {userData.firstName} {userData.lastName}
        </TagLabel>
      </Tag>
    );
  };

  const SectionCard: React.FC<{
    title: string;
    accent: string;
    children: React.ReactNode;
    action?: React.ReactNode;
  }> = ({ title, accent, children, action }) => (
    <Box
      bg={cardBg}
      borderRadius="xl"
      p={{ base: 4, md: 5 }}
      border="1px solid"
      borderColor={subtleBorder}
      boxShadow="sm"
      h="full"
    >
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={2}>
          <Box w={2.5} h={2.5} bg={accent} borderRadius="full" />
          <Heading size="sm" color={mutedText} textTransform="uppercase" letterSpacing="wide">
            {title}
          </Heading>
        </HStack>
        {action}
      </Flex>
      {children}
    </Box>
  );

  const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box>
      <Text fontSize="xs" fontWeight="semibold" color={mutedText} textTransform="uppercase" letterSpacing="wide" mb={1}>
        {label}
      </Text>
      <Box>{children}</Box>
    </Box>
  );

  const descriptionStyles = {
    whiteSpace: 'pre-line' as const,
    wordBreak: 'break-word' as const,
    '& ul': { marginLeft: '20px', paddingLeft: 0, listStyleType: 'disc', marginBottom: '16px' },
    '& ol': { marginLeft: '20px', paddingLeft: 0, listStyleType: 'decimal', marginBottom: '16px' },
    '& li': { marginBottom: '8px', paddingLeft: '8px', '& p': { marginBottom: '10px' } },
    '& a': { color: '#3097e3', textDecoration: 'underline', '&:hover': { color: '#2082d1' } },
    '& p': { marginBottom: '15px' },
    '& .task-mention': { cursor: 'pointer !important', '&:hover': { backgroundColor: '#bbdefb !important' } },
    '& .task-mention-not-found': { opacity: 0.6 },
  };

  return (
    <Box w="100%" bg={pageBg} minH="100%">
      <Box mx="auto" py={{ base: 4, md: 6 }}>
        {showTopHeader && (
          <Box
            bg={cardBg}
            borderRadius="2xl"
            p={{ base: 5, md: 6 }}
            mb={6}
            border="1px solid"
            borderColor={subtleBorder}
            boxShadow="sm"
          >
            <Flex
              direction={{ base: 'column', lg: 'row' }}
              justify="space-between"
              align={{ base: 'stretch', lg: 'flex-start' }}
              gap={6}
            >
              <VStack align="start" spacing={4} flex={1}>
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color={mutedText} mb={1}>
                    Task Details
                  </Text>
                  <Heading size="lg" lineHeight="shorter">
                    {task.taskTitle}
                  </Heading>
                </Box>

                <Wrap spacing={2}>
                  <WrapItem>
                    <Badge colorScheme="blue" variant="subtle" px={3} py={1} borderRadius="full" fontSize="xs">
                      {task.type}
                    </Badge>
                  </WrapItem>
                  <WrapItem>
                    <Badge colorScheme={getStatusColor(task.status)} px={3} py={1} borderRadius="full" fontSize="xs">
                      {task.status}
                    </Badge>
                  </WrapItem>
                  <WrapItem>
                    <Badge colorScheme={getPriorityColor(task.priority)} px={3} py={1} borderRadius="full" fontSize="xs">
                      {task.priority} priority
                    </Badge>
                  </WrapItem>
                  <WrapItem>
                    <Badge variant="outline" colorScheme="gray" px={3} py={1} borderRadius="full" fontSize="xs">
                      {task.taskId}
                    </Badge>
                  </WrapItem>
                </Wrap>

                <HStack spacing={2} flexWrap="wrap">
                  <Text fontSize="sm" color={mutedText}>Created by</Text>
                  {task.createdBy ? (
                    renderUserTag(task.createdBy as UserRef)
                  ) : (
                    <Text fontSize="sm" color="gray.400">Not specified</Text>
                  )}
                  {task.createdAt && (
                    <Text fontSize="sm" color="gray.400">
                      · {formatDate(task.createdAt)}
                    </Text>
                  )}
                </HStack>
              </VStack>

              {showHeaderButtons && (
                <HStack spacing={3} alignSelf={{ base: 'stretch', lg: 'flex-start' }} justify={{ base: 'stretch', sm: 'flex-end' }}>
                  {canPerformWrites && canEditCurrentTask && (
                    <PermissionGuard permission={Permission.EDIT_TASKS}>
                      <Button
                        onClick={handleEditTask}
                        colorScheme="blue"
                        variant="outline"
                        leftIcon={<EditIcon />}
                        borderRadius="full"
                        flex={{ base: 1, sm: 'initial' }}
                      >
                        Edit Task
                      </Button>
                    </PermissionGuard>
                  )}
                  <Button
                    onClick={onClose}
                    colorScheme="gray"
                    variant="solid"
                    leftIcon={<ArrowBackIcon />}
                    borderRadius="full"
                    flex={{ base: 1, sm: 'initial' }}
                  >
                    Back to Tasks
                  </Button>
                </HStack>
              )}
            </Flex>
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4} mb={6}>
          <SectionCard title="Financial" accent="green.400">
            <SimpleGrid columns={2} spacing={4}>
              <InfoRow label="Quoted">
                <Text fontWeight="semibold" color="green.600" fontSize="lg">
                  {formatCurrency(task.quotedPrice)}
                </Text>
              </InfoRow>
              <InfoRow label="Agreed">
                <Text fontWeight="semibold" color="green.600" fontSize="lg">
                  {formatCurrency(task.agreedPrice)}
                </Text>
              </InfoRow>
            </SimpleGrid>
          </SectionCard>

          <SectionCard title="Timeline" accent="orange.400">
            <SimpleGrid columns={1} spacing={3}>
              <InfoRow label="Requested">{formatDate(task.dateRequested)}</InfoRow>
              <InfoRow label="Started">{formatDate(task.dateStarted)}</InfoRow>
              <InfoRow label="Due">
                <Text color={task.dueDate ? 'red.500' : 'inherit'} fontWeight={task.dueDate ? 'semibold' : 'normal'}>
                  {formatDate(task.dueDate)}
                </Text>
              </InfoRow>
            </SimpleGrid>
          </SectionCard>

          <SectionCard title="Project" accent="blue.400">
            <InfoRow label="Linked project">
              <Text fontWeight="medium" color="blue.600" noOfLines={2}>
                {getProjectName(task)}
              </Text>
            </InfoRow>
          </SectionCard>

          <SectionCard title="People" accent="purple.400">
            <VStack align="stretch" spacing={3}>
              <InfoRow label="Assigned to">
                <Wrap spacing={2}>
                  {task.assignedToUser && task.assignedToUser.length > 0 ? (
                    task.assignedToUser.map((userRef: UserRef) => renderUserTag(userRef))
                  ) : (
                    <Text color="gray.400" fontSize="sm">Not assigned</Text>
                  )}
                </Wrap>
              </InfoRow>
              <InfoRow label="Authorized by">
                <Wrap spacing={2}>
                  {task.authBy && task.authBy.length > 0 ? (
                    task.authBy.map((userRef: UserRef) => renderUserTag(userRef))
                  ) : (
                    <Text color="gray.400" fontSize="sm">Not specified</Text>
                  )}
                </Wrap>
              </InfoRow>
            </VStack>
          </SectionCard>
        </SimpleGrid>

        <Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 360px' }} gap={6} alignItems="start">
          <GridItem>
            <VStack spacing={6} align="stretch">
              <SectionCard title="Description" accent="teal.400">
                <Box
                  bg={insetBg}
                  p={4}
                  borderRadius="lg"
                  minH="160px"
                  maxH="500px"
                  overflowY="auto"
                  border="1px solid"
                  borderColor={subtleBorder}
                >
                  {task.description ? (
                    <TaskMentionDisplay
                      text={task.description}
                      tasks={allTasks || []}
                      onTaskClick={(linkedTaskId) => navigate(`/dashboard/task/${linkedTaskId}`)}
                      sx={descriptionStyles}
                    />
                  ) : (
                    <Text color="gray.500" fontStyle="italic">No description provided</Text>
                  )}
                </Box>
              </SectionCard>

              <SubtaskManagement taskId={taskId} />

              <SectionCard
                title="Files"
                accent="green.400"
                action={
                  <HStack spacing={2}>
                    {canPerformWrites && (
                      <Button
                        onClick={handleCloudinaryTaskFileUpload}
                        colorScheme="green"
                        size="sm"
                        leftIcon={<AttachmentIcon />}
                        borderRadius="full"
                      >
                        Upload
                      </Button>
                    )}
                    <IconButton
                      aria-label={filesExpanded ? 'Collapse files' : 'Expand files'}
                      icon={filesExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={toggleFilesExpanded}
                      colorScheme="green"
                    />
                  </HStack>
                }
              >
                {filesExpanded ? (
                  files.length === 0 ? (
                    <Box textAlign="center" py={10} color="gray.400" bg={insetBg} borderRadius="lg">
                      <AttachmentIcon boxSize={8} mb={2} mx="auto" />
                      <Text>No files uploaded yet</Text>
                    </Box>
                  ) : (
                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
                      {files.map((f: any) => {
                        const ext = f.url ? f.url.split('.').pop().toLowerCase().split('?')[0] : '';
                        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
                        const isPdf = ext === 'pdf';
                        const isDoc = ['doc', 'docx'].includes(ext);

                        return (
                          <Box
                            key={f._id}
                            bg={insetBg}
                            borderRadius="lg"
                            border="1px solid"
                            borderColor={subtleBorder}
                            p={4}
                            _hover={{ borderColor: 'green.300', boxShadow: 'md' }}
                            transition="all 0.2s"
                          >
                            <VStack spacing={3} align="stretch">
                              <HStack justify="space-between">
                                <Badge
                                  colorScheme={isImage ? 'teal' : isPdf ? 'red' : isDoc ? 'purple' : 'gray'}
                                  borderRadius="full"
                                >
                                  {isImage ? 'Image' : isPdf ? 'PDF' : isDoc ? 'DOC' : ext.toUpperCase()}
                                </Badge>
                                <Text fontSize="xs" color="gray.500" noOfLines={1} maxW="120px">
                                  {f.filename || f.url?.split('/').pop()?.split('?')[0]}
                                </Text>
                              </HStack>

                              <Box
                                h="160px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bg={cardBg}
                                borderRadius="md"
                                border="1px solid"
                                borderColor={subtleBorder}
                                overflow="hidden"
                              >
                                {isImage ? (
                                  <img
                                    src={f.url}
                                    alt={f.filename || 'File preview'}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => setPreviewFileUrl(f.url)}
                                  />
                                ) : (
                                  <Button
                                    size="sm"
                                    colorScheme={isPdf ? 'red' : isDoc ? 'purple' : 'gray'}
                                    onClick={() => setPreviewFileUrl(f.url)}
                                    variant="outline"
                                  >
                                    Preview
                                  </Button>
                                )}
                              </Box>

                              <Text fontSize="xs" color={mutedText} noOfLines={2} minH="32px">
                                {f.fileDescription || 'No description'}
                              </Text>

                              <HStack spacing={2}>
                                <Button
                                  as="a"
                                  href={f.url}
                                  download
                                  target="_blank"
                                  size="xs"
                                  colorScheme="blue"
                                  variant="outline"
                                  flex={1}
                                >
                                  Download
                                </Button>
                                {canPerformWrites && user && (user._id === f.uploader || user.role === 'admin') && (
                                  <IconButton
                                    aria-label="Delete file"
                                    icon={<DeleteIcon />}
                                    size="xs"
                                    colorScheme="red"
                                    variant="outline"
                                    onClick={() => handleDeleteFile(f._id)}
                                  />
                                )}
                              </HStack>
                            </VStack>
                          </Box>
                        );
                      })}
                    </SimpleGrid>
                  )
                ) : (
                  <Text fontSize="sm" color={mutedText}>
                    {files.length} file{files.length === 1 ? '' : 's'} attached. Expand to view.
                  </Text>
                )}
              </SectionCard>

              <SectionCard
                title="Comments"
                accent="blue.400"
                action={
                  <HStack spacing={2}>
                    <Select
                      size="sm"
                      w="130px"
                      value={commentSortOrder}
                      onChange={(e) => handleCommentSortChange(e.target.value as 'asc' | 'desc')}
                      bg={insetBg}
                      borderColor={subtleBorder}
                      borderRadius="md"
                    >
                      <option value="desc">Newest first</option>
                      <option value="asc">Oldest first</option>
                    </Select>
                    <IconButton
                      aria-label={commentsExpanded ? 'Collapse comments' : 'Expand comments'}
                      icon={commentsExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={toggleCommentsExpanded}
                      colorScheme="blue"
                    />
                  </HStack>
                }
              >
                {commentsExpanded ? (
                  <CommentsSection
                    comments={sortedComments}
                    users={users}
                    addCommentMutation={{
                      ...addCommentMutation,
                      editCommentMutation,
                    }}
                    deleteCommentMutation={deleteCommentMutation}
                    refetchComments={refetchComments}
                    addReplyMutation={addReplyMutation}
                    deleteReplyMutation={deleteReplyMutation}
                    editReplyMutation={editReplyMutation}
                    user={user}
                    taskId={taskId}
                  />
                ) : (
                  <Text fontSize="sm" color={mutedText}>
                    {comments.length} comment{comments.length === 1 ? '' : 's'}. Expand to view and reply.
                  </Text>
                )}
              </SectionCard>
            </VStack>
          </GridItem>

          <GridItem position={{ xl: 'sticky' }} top={{ xl: 6 }}>
            <VStack spacing={4} align="stretch">
              <SectionCard title="At a glance" accent="blue.400">
                <VStack align="stretch" spacing={4} divider={<Divider />}>
                  <InfoRow label="Status">
                    <Badge colorScheme={getStatusColor(task.status)} borderRadius="full" px={3}>
                      {task.status}
                    </Badge>
                  </InfoRow>
                  <InfoRow label="Priority">
                    <Badge colorScheme={getPriorityColor(task.priority)} borderRadius="full" px={3}>
                      {task.priority}
                    </Badge>
                  </InfoRow>
                  <InfoRow label="Type">{task.type}</InfoRow>
                  <InfoRow label="Project">{getProjectName(task)}</InfoRow>
                  <InfoRow label="Due date">
                    <Text color={task.dueDate ? 'red.500' : 'inherit'} fontWeight={task.dueDate ? 'semibold' : 'normal'}>
                      {formatDate(task.dueDate)}
                    </Text>
                  </InfoRow>
                  <InfoRow label="Last updated">{formatDate(task.updatedAt)}</InfoRow>
                </VStack>
              </SectionCard>

              <SectionCard title="Assignment" accent="purple.400">
                <VStack align="stretch" spacing={3}>
                  <InfoRow label="Assigned to">
                    <Wrap spacing={2}>
                      {task.assignedToUser && task.assignedToUser.length > 0 ? (
                        task.assignedToUser.map((userRef: UserRef) => renderUserTag(userRef))
                      ) : (
                        <Text color="gray.400" fontSize="sm">Not assigned</Text>
                      )}
                    </Wrap>
                  </InfoRow>
                  <InfoRow label="Authorized by">
                    <Wrap spacing={2}>
                      {task.authBy && task.authBy.length > 0 ? (
                        task.authBy.map((userRef: UserRef) => renderUserTag(userRef))
                      ) : (
                        <Text color="gray.400" fontSize="sm">Not specified</Text>
                      )}
                    </Wrap>
                  </InfoRow>
                </VStack>
              </SectionCard>
            </VStack>
          </GridItem>
        </Grid>
      </Box>

      {/* File Description Modal */}
      <Modal 
        isOpen={isFileDescModalOpen} 
        onClose={() => {
          setIsFileDescModalOpen(false);
          setPendingFiles([]);
          setIsBatchUploading(false);
        }} 
        isCentered 
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {pendingFiles.length > 0 ? `Add Descriptions for ${pendingFiles.length} Files` : 'Add File Description'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {pendingFiles.length > 0 ? (
              <VStack spacing={4} align="stretch">
                <Text mb={2}>You can add optional descriptions for each file:</Text>
                {pendingFiles.map((file, index) => (
                  <Box key={file.id} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        {file.originalname || file.filename}
                      </Text>
                      <Badge colorScheme="blue" size="sm">
                        {file.mimetype?.split('/')[1]?.toUpperCase() || 'FILE'}
                      </Badge>
                    </HStack>
                    <Input
                      placeholder="File description (optional)"
                      value={file.fileDescription}
                      onChange={(e) => handleUpdateFileDescription(file.id, e.target.value)}
                      size="sm"
                    />
                  </Box>
                ))}
              </VStack>
            ) : (
              <>
                <Text mb={2}>You can add an optional description for this file:</Text>
                <Input
                  placeholder="File description (optional)"
                  value={pendingFileDesc}
                  onChange={e => setPendingFileDesc(e.target.value)}
                  autoFocus
                />
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              onClick={() => {
                setIsFileDescModalOpen(false);
                setPendingFiles([]);
                setIsBatchUploading(false);
              }} 
              mr={3} 
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isLoading={isBatchUploading}
              onClick={pendingFiles.length > 0 ? handleSaveBatchFiles : async () => {
                if (pendingFileInfo) {
                  try {
                    await saveCloudinaryTaskFile(taskId, {
                      ...pendingFileInfo,
                      fileDescription: pendingFileDesc,
                    });
                    setIsFileDescModalOpen(false);
                    setPendingFileUrl(null);
                    setPendingFileInfo(null);
                    setPendingFileDesc('');
                    refetchFiles();
                    toast({ title: 'File uploaded', status: 'success', duration: 2000 });
                  } catch (err: any) {
                    toast({ title: 'Failed to save file', description: err?.response?.data?.message || err.message, status: 'error', duration: 4000 });
                  }
                }
              }}
            >
              {pendingFiles.length > 0 ? `Save ${pendingFiles.length} Files` : 'Add File'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* File/Image Preview Modal */}
      {previewFileUrl && (
        <Box 
          position="fixed" 
          top={0} 
          left={0} 
          w="100vw" 
          h="100vh" 
          bg="rgba(0, 0, 0, 0.8)" 
          zIndex={2000} 
          display="flex" 
          alignItems="center" 
          justifyContent="center"
        >
          <Box 
            position="relative" 
            bg="white" 
            p={2} 
            borderRadius="md" 
            boxShadow="2xl" 
            maxW="90vw" 
            maxH="90vh"
          >
            {/* File preview content */}
            {(() => {
              const ext = previewFileUrl.split('.').pop()?.toLowerCase().split('?')[0] || '';
              if (["jpg","jpeg","png","gif","bmp","webp"].includes(ext)) {
                return <img src={previewFileUrl} alt="Preview" style={{ maxWidth: '80vw', maxHeight: '80vh', borderRadius: 8 }} />;
              } else if (ext === 'pdf') {
                return <iframe src={previewFileUrl} title="PDF Preview" width="80vw" height="80vh" style={{ border: 'none', borderRadius: 8, background: 'white' }} />;
              } else if (["doc","docx"].includes(ext)) {
                return <Box p={4}><Text>Preview not supported for DOC/DOCX. <a href={previewFileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce' }}>Download file</a></Text></Box>;
              } else {
                return <Box p={4}><Text>No preview available for this file type.</Text></Box>;
              }
            })()}
            <Button 
              position="absolute" 
              top={2} 
              right={2} 
              size="sm" 
              colorScheme="red" 
              onClick={() => setPreviewFileUrl(null)}
            >
              Close
            </Button>
          </Box>
        </Box>
      )}

      <TaskModal
        isOpen={isEditModalOpen}
        onClose={onEditModalClose}
        formData={taskFormData}
        onFormChange={(data) => setTaskFormData({ ...taskFormData, ...data })}
        onSubmit={handleSubmitTask}
        isLoading={updateTaskMutation.isPending}
        selectedProject={selectedProject}
        onProjectChange={setSelectedProject}
        title="Edit Task"
      />
    </Box>
  );
};

export default TaskDetails;